
import React from 'react';
import { Edit, Trash2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CategoryIcon } from '../../shared/CategoryIcon';
import { SessionType } from '@/types/assistant';
import { TOPIC_CONFIG } from '@/contexts/AiAssistantContext';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

interface SidebarSessionProps {
  session: SessionType;
  isActive: boolean;
  isNewestSession: boolean;
  lastMessageContent: string;
  onSessionClick: (sessionId: string) => void;
  onRenameClick: (e: React.MouseEvent, session: SessionType) => void;
  onDeleteClick: (e: React.MouseEvent, session: SessionType) => void;
}

export const SidebarSession: React.FC<SidebarSessionProps> = ({
  session,
  isActive,
  isNewestSession,
  lastMessageContent,
  onSessionClick,
  onRenameClick,
  onDeleteClick
}) => {
  const topicConfig = TOPIC_CONFIG[session.topic];

  return (
    <motion.div 
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.2 }}
      className={cn(
        "group relative flex flex-col p-2 rounded-md cursor-pointer transition-all",
        isActive 
          ? `${topicConfig.bgColor} backdrop-blur-sm border ${topicConfig.borderColor} shadow-md` 
          : "hover:bg-white/5"
      )}
      onClick={() => onSessionClick(session.id)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <motion.div
            whileHover={{ rotate: 5 }}
            whileTap={{ scale: 0.95 }}
          >
            <CategoryIcon 
              category={session.topic} 
              className={isActive ? topicConfig.color : "text-foreground"}
            />
          </motion.div>
          <span className={cn(
            "font-medium truncate text-sm",
            isActive ? topicConfig.color : ""
          )}>
            {session.name_session}
          </span>
          
          {/* Only show a sparkle on the most recent session */}
          {isNewestSession && !isActive && (
            <motion.div
              animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 0.9, 1] }}
              transition={{ repeat: Infinity, duration: 2, repeatType: "reverse" }}
            >
              <Sparkles size={12} className="text-yellow-300" />
            </motion.div>
          )}
        </div>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity",
              isActive ? "text-foreground/80" : "text-muted-foreground"
            )}
            onClick={(e) => onRenameClick(e, session)}
            title="Rename session"
          >
            <motion.div whileHover={{ scale: 1.2 }} whileTap={{ scale: 0.9 }}>
              <Edit className="h-3.5 w-3.5" />
            </motion.div>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity",
              isActive ? "text-foreground/80" : "text-muted-foreground"
            )}
            onClick={(e) => onDeleteClick(e, session)}
            title="Delete session"
          >
            <motion.div whileHover={{ scale: 1.2 }} whileTap={{ scale: 0.9 }}>
              <Trash2 className="h-3.5 w-3.5" />
            </motion.div>
          </Button>
        </div>
      </div>
      
      <p className="text-xs text-muted-foreground truncate mt-1">
        {lastMessageContent}
      </p>
      
      <p className="text-xs text-muted-foreground mt-1">
        {new Date(session.updated_at).toLocaleDateString()}
      </p>
      
      {/* Glow effect for active session */}
      {isActive && (
        <motion.div 
          className="absolute inset-0 rounded-md -z-10"
          initial={{ opacity: 0 }}
          animate={{ 
            opacity: [0.5, 0.8, 0.5], 
            boxShadow: [
              `0 0 10px 0px ${topicConfig.color}30`,
              `0 0 15px 2px ${topicConfig.color}40`,
              `0 0 10px 0px ${topicConfig.color}30`
            ]
          }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      )}
    </motion.div>
  );
};
